import {
  requestLogin,
  requestLogout,
  requestPasswordChange,
  requestPasswordResetConfirm,
  requestPasswordResetEmail,
  requestPasswordResetTokenVerify,
  requestResendEmailCode,
  requestVerifyEmailCode,
  type LoginRequest,
} from '../api/authApi';
import { clearAccessToken, getAccessToken, saveAccessToken } from '../api/client';
import { clearStoredUserDisplayName } from './managerService';

const LOGIN_TRX_ID_STORAGE_KEY = 'loginTrxId';
const LATEST_LOGIN_TRX_ID_STORAGE_KEY = 'latestLoginTrxId';
const STALE_LOGIN_ATTEMPT_MESSAGE =
  '다른 탭 또는 창에서 새로운 로그인이 시작되었습니다. 다시 로그인해주세요.';

export class LoginAttemptInvalidError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginAttemptInvalidError';
  }
}

export async function login(request: LoginRequest) {
  clearAccessToken();
  clearStoredUserDisplayName();

  const response = await requestLogin(request);

  sessionStorage.setItem(LOGIN_TRX_ID_STORAGE_KEY, response.trxId);
  localStorage.setItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY, response.trxId);

  return response;
}

export async function verifyLoginCode(code: string) {
  const trxId = sessionStorage.getItem(LOGIN_TRX_ID_STORAGE_KEY);
  const latestTrxId = localStorage.getItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY);

  if (!trxId) {
    throw new LoginAttemptInvalidError('로그인 정보가 없습니다. 다시 로그인해주세요.');
  }

  if (latestTrxId && trxId !== latestTrxId) {
    sessionStorage.removeItem(LOGIN_TRX_ID_STORAGE_KEY);
    throw new LoginAttemptInvalidError(STALE_LOGIN_ATTEMPT_MESSAGE);
  }

  const response = await requestVerifyEmailCode({
    trxId,
    code,
  });

  saveAccessToken(response.accessToken);
  sessionStorage.removeItem(LOGIN_TRX_ID_STORAGE_KEY);

  return response;
}

export async function resendLoginCode() {
  const trxId = sessionStorage.getItem(LOGIN_TRX_ID_STORAGE_KEY);
  const latestTrxId = localStorage.getItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY);

  if (!trxId) {
    throw new LoginAttemptInvalidError('로그인 정보가 없습니다. 다시 로그인해주세요.');
  }

  if (latestTrxId && trxId !== latestTrxId) {
    sessionStorage.removeItem(LOGIN_TRX_ID_STORAGE_KEY);
    throw new LoginAttemptInvalidError(STALE_LOGIN_ATTEMPT_MESSAGE);
  }

  return requestResendEmailCode({ trxId });
}

export function sendPasswordResetEmail(email: string) {
  return requestPasswordResetEmail({ email });
}

export function changePassword(currentPassword: string, newPassword: string) {
  return requestPasswordChange({
    currentPassword,
    newPassword,
  });
}

export async function verifyPasswordResetToken(urlToken: string) {
  return requestPasswordResetTokenVerify(urlToken);
}

export async function resetPasswordByEmailLink(urlToken: string, password: string) {
  const response = await requestPasswordResetConfirm({
    password,
    urlToken,
  });

  void logout();

  return response;
}

export async function logout() {
  if (getAccessToken()) {
    try {
      await requestLogout();
    } catch {
      // 서버 호출이 실패해도 로컬 로그아웃은 계속 진행한다.
    }
  }

  clearAccessToken();
  sessionStorage.removeItem(LOGIN_TRX_ID_STORAGE_KEY);
  localStorage.removeItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY);
  clearStoredUserDisplayName();
}

export function hasLoginTrxId() {
  const trxId = sessionStorage.getItem(LOGIN_TRX_ID_STORAGE_KEY);
  const latestTrxId = localStorage.getItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY);

  return Boolean(trxId) && (!latestTrxId || trxId === latestTrxId);
}

export function getLoginAttemptInvalidMessage() {
  const trxId = sessionStorage.getItem(LOGIN_TRX_ID_STORAGE_KEY);
  const latestTrxId = localStorage.getItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY);

  if (!trxId) {
    return '로그인 정보가 없습니다. 다시 로그인해주세요.';
  }

  if (latestTrxId && trxId !== latestTrxId) {
    sessionStorage.removeItem(LOGIN_TRX_ID_STORAGE_KEY);
    return STALE_LOGIN_ATTEMPT_MESSAGE;
  }

  return '';
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}
